import React from 'react';
import { Alert, Box, Stack } from '@mui/material';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { useProviderHealthQuery } from '../../API/Dashboard';

const getPauseReasonLabel = (reason?: string) => {
  switch (reason) {
    case 'VAPI_OUT_OF_CREDITS':
      return 'Vapi Wallet Balance Negative / Out of Credits';
    case 'VAPI_KEY_INVALID':
      return 'Vapi API Key Invalid or Unauthorized';
    case 'TWILIO_ACCOUNT_SUSPENDED':
      return 'Twilio Account Suspended or Unpaid';
    case 'TWILIO_SUSPENDED_OR_EMPTY':
      return 'Twilio Account Balance Empty / Suspended';
    case 'TWILIO_AUTH_ERROR':
      return 'Twilio Authentication Failed (Code 20003)';
    case 'OUT_OF_CREDITS':
      return 'Voice Minute Plan Allowance Exhausted';
    default:
      return reason || 'Provider Issue';
  }
};

export const ProviderHealthBanner: React.FC = () => {
  const { data: providerHealth } = useProviderHealthQuery();

  if (!providerHealth) return null;

  const vapiFailed = providerHealth.vapi && !providerHealth.vapi.healthy;
  const twilioFailed = providerHealth.twilio && !providerHealth.twilio.healthy;
  const pausedCampaigns = providerHealth.pausedCampaigns || [];
  const hasPausedCampaigns = (providerHealth.pausedCampaignsCount || 0) > 0 || pausedCampaigns.length > 0;

  if (!vapiFailed && !twilioFailed && !hasPausedCampaigns) {
    return null;
  }

  // Format issue summary for paused campaigns
  const reasonMap: Record<string, string[]> = {};
  pausedCampaigns.forEach((c: any) => {
    const key = c.pauseReason || 'Unknown';
    if (!reasonMap[key]) reasonMap[key] = [];
    reasonMap[key].push(c.title ? `"${c.title}"` : `ID ${c.id}`);
  });

  const issueSummaries = Object.entries(reasonMap).map(([reason, titles]) => {
    const label = getPauseReasonLabel(reason);
    return `${label} [${titles.join(' , ')}]`;
  });

  return (
    <Box sx={{ mb: 3 }}>
      <Stack spacing={1.5}>
        {vapiFailed && (
          <Alert
            severity="error"
            icon={<ShieldAlert size={20} />}
            sx={{ borderRadius: '10px', fontWeight: 600, boxShadow: '0 2px 10px rgba(211,47,47,0.15)' }}
          >
            🚨 <strong>Vapi Provider Failure ({providerHealth.vapi.reason}):</strong> {providerHealth.vapi.message}
          </Alert>
        )}

        {twilioFailed && (
          <Alert
            severity="warning"
            icon={<AlertTriangle size={20} />}
            sx={{ borderRadius: '10px', fontWeight: 600, boxShadow: '0 2px 10px rgba(237,108,2,0.15)' }}
          >
            ⚠️ <strong>Twilio Provider Failure ({providerHealth.twilio.reason}):</strong> {providerHealth.twilio.message}
          </Alert>
        )}

        {hasPausedCampaigns && (
          <Alert
            severity="warning"
            icon={<AlertTriangle size={20} />}
            sx={{ borderRadius: '10px', fontWeight: 600, boxShadow: '0 2px 10px rgba(237,108,2,0.15)' }}
          >
            ⚠️ <strong>Provider Alert:</strong> {providerHealth.pausedCampaignsCount || pausedCampaigns.length} campaign(s) auto-paused.
            {issueSummaries.length > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 0.5, fontWeight: 500, fontSize: '0.9rem' }}>
                <strong>Issue Details:</strong> {issueSummaries.join(' | ')}
              </Box>
            )}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};
export default ProviderHealthBanner;
